import React, { useEffect, useState } from 'react';
import { GetElsLookupsState } from '../../redux/reducers/getLookups/getElsLookupsReducer';
import { useDispatch, useSelector } from 'react-redux';
import "../GuidedExperience/GuidedExperienceWizard.css";
import { Trans, useTranslation } from 'react-i18next';
import i18n from '../../i18n';
import Button from '../common/Button/Button';
import ApartmentStyleWizard from './ApartmentStyleWizard';
import ApartmentUnitsWizard from './ApartmentUnits';
import OtherUnitsWizard from './OtherUnits';
import NumberOfParkingsWizard from './NumberOfParkingsWizard';
import ParkingRateWizard from './ParkingRateWizard';
import { toast } from 'react-toastify';
import MultiresSummaryWizard from './MultiresSummaryWizard';
import { ProfileAttributesState } from '../../redux/reducers/getProfile/profileAttributes/getAttributesReducer';
import { getProfileAttributes, putProfileAttributes, resetGetProfileAttributes } from '../../redux/actions/profile/profile';
import { ProfileOverviewState } from '../../redux/reducers/getProfile/getProfileOverviewReducer';
import { SaveResultState } from '../../redux/reducers/getSaveData/saveResultReducer';
import LoadingSpinner from '../common/LoadingSpinner/LoadingSpinner';
import { resetRollDownIncomeUnits, upsertBatchIncomeUnits } from '../../redux/actions/incomeUnit/incomeUnit';
import ConfirmDeleteWizard from './ConfirmDeleteWizard';
import { BaseAsyncReducerState } from '../../redux/types';
import ConfirmUnitsWizard from './ConfirmUnitsWizard';
import Modal from '../common/Modal/Modal';
import { Option } from '../common/Dropdown/Dropdown';
import { selectDeleteIncomeUnit, selectELSLookups, selectProfileGetAttributes, selectProfileGetOverview, selectRollDownUnits, selectSaveResult } from '../../redux/selectors/exportSelectorPaths';
import { ProfileDetailsState } from '../../redux/reducers/getProfile/getProfileDetailsReducer';
import { getDefaultUnit } from '../../types/unit/IncomeUnit';
import { WizardExitTrigger, WizardInteractionEventPayload } from '../../types/GA4Events/WizardEventParams';
import trackWizardEvent, { createWizardEventPayload } from '../../utils/GA4EventHelpers/trackWizardEvent';
import AffordableHousingWizard from './AffordableHousingWizard';
import { resetSaveResult, resetSaving } from '../../redux/actions/saveData/saveData';
import { getFormErrorByName, useFormErrorContext } from '../../utils/context/FormErrorContext';
import { putAptFlagAffordableHousing } from '../../redux/actions/profile/profileDetails';

type affordableHousingFields = {
    hasAffordableHousingFlag: boolean | null;
    affordableHousingAgreementCodes: string[] | null;
    affordableHousingCount: number | null;
}

interface MultiresWizardProps {
    snapshotId: number;
    onCancel: (active_step: number, exit_method: WizardExitTrigger, submitted: boolean) => void;
    unitData: Record<string, unknown>[];
    deleteAllUnitsTrigger: React.Dispatch<React.SetStateAction<boolean>>;
    rollDownAllUnitsTrigger: React.Dispatch<React.SetStateAction<boolean>>;
    isBasePropType: boolean;
    styleOptions: Option[];
    unitTypeSubtypeMap: Record<string, Option[]>;
    profileDetails: ProfileDetailsState;
    showResidentialQuestion: boolean;
    bathsOptions: ({
        value: string;
        text: string;
    } | {
        value: number;
        text: string;
    } | Option)[];
    sendActiveStepToParent: (step: number) => void;
    showAffordableHousingQuestion: boolean;
    hasApartmentFlag: boolean | null;
    affordableHousingFields: affordableHousingFields;
    propertyCode: string;
}

export default function MultiresWizard(props: MultiresWizardProps): JSX.Element {
    const { errors, removeError, clearErrors } = useFormErrorContext()
    const { t } = useTranslation("guided_wizard");
    const dispatch = useDispatch();
    const [currentWizardPage, setCurrentWizardPage] = useState(0);
    const [submitted, setSubmitted] = useState(false);
    const [attributesRequested, setAttributesRequested] = useState(false);
    const [parkingAssigned, setParkingAssigned] = useState(false);
    const propertyType: "multires" | undefined = sessionStorage.getItem("propertyCode") ? "multires" : undefined
    const hasParking: boolean = sessionStorage.getItem("propertyCode") ? (["340", "341", "352", "361", "602", "470", "472", "478"].includes(sessionStorage.getItem("propertyCode") as string)) : false
    const elsLookupsState: GetElsLookupsState = useSelector(selectELSLookups);
    const profileOverview: ProfileOverviewState = useSelector(selectProfileGetOverview)
    const profileAttributes: ProfileAttributesState = useSelector(selectProfileGetAttributes)
    const saveResultState: SaveResultState = useSelector(selectSaveResult)
    const rollDownUnitState: BaseAsyncReducerState = useSelector(selectRollDownUnits)
    const deleteUnitsState: BaseAsyncReducerState = useSelector(selectDeleteIncomeUnit)
    const apartmentStyles = elsLookupsState.dropdowns ? (elsLookupsState.dropdowns["unitStyleType"] as Record<string, unknown>[]) : [];
    const multiresUnitSubtype = elsLookupsState.dropdowns ? (elsLookupsState.dropdowns["multiresSubType"] as Record<string, unknown>[]) : [];
    const multiresRowHouseUnitSubtype = elsLookupsState.dropdowns ? (elsLookupsState.dropdowns["multiresRowHouseSubType"] as Record<string, unknown>[]) : [];
    const [showAffordableHousingQuestion, setShowAffordableHousingQuestion] = useState(props.showAffordableHousingQuestion)
    const [apartmentStyleArray, setApartmentStyleArray] = useState(apartmentStyles.reduce((apartmentStyleRecord, apartmentStyleOption) => ({
        [apartmentStyleOption["unitStyleCode"] as string]:
            false, ...apartmentStyleRecord
    }), {}));
    const [apartmentNumber, setApartmentNumber] = useState(apartmentStyles.reduce((apartmentStyleRecord, apartmentStyleOption) => ({
        [apartmentStyleOption["unitStyleCode"] as string]:
            (apartmentStyleOption["unitStyleCode"] === apartmentStyles[0]["unitStyleCode"] ? multiresUnitSubtype : multiresRowHouseUnitSubtype
            ).reduce((unitSubtypeRecord, unitSubtype3Option) => ({
                [unitSubtype3Option["unitSubtypeCode"] as string]:
                    0, ...unitSubtypeRecord
            }), {}), ...apartmentStyleRecord
    }), {}));
    const [confirmImport, setConfirmImport] = useState<"question" | "import" | "new" | "affordable">(props.unitData.length > 0 ? "question" : "new");
    const [confirmedArr, setConfirmedArr] = useState<number[]>([]);
    const [showDeleteWarning, setShowDeleteWarning] = useState(false);
    const [showRollDownWarning, setShowRollDownWarning] = useState(false);
    const [hasAffordableHousingFlag, setHasAffordableHousingFlag] = useState<boolean | null>(null);
    const [units, setUnits] = useState(props.unitData);
    const [parkings, setParkings] = useState<Record<string, unknown>>({
        'residentialParkingTotal': 0,
        'residentialSurfaceParkingTotal': 0,
        'residentialSurfaceParkingNotInRent': 0,
        'residentialSurfaceParkingMonthlyRate': null,
        'residentialSurfaceParkingInRent': 0,
        'residentialIndoorParkingTotal': 0,
        'residentialIndoorParkingNotInRent': 0,
        'residentialIndoorParkingMonthlyRate': null,
        'residentialIndoorParkingInRent': 0,
    });
    const aptTypeMap = {
        "apt": ["301", "302", "303", "304", "305", "306", "307", "308", "309", "343", "352"],
        "bst": ["310", "311", "312", "313", "314", "315"],
        "pen": ["327", "328", "329", "330", "331", "332", "333", "334"],
        "lrg": ["317", "318", "319", "320", "321", "322", "323", "324"],
        "sml": ["344", "345", "346", "347", "348", "349", "350", "351"]
    }

    const { onCancel, sendActiveStepToParent } = props;

    useEffect(() => {
        dispatch(resetGetProfileAttributes());
        dispatch(getProfileAttributes(profileOverview.profile.profile_type_code, props.snapshotId));
        setAttributesRequested(true);
        // eslint-disable-next-line
    }, []);

    useEffect(() => {
        const currentRollNumber = sessionStorage.getItem("rollNumber")
        if (currentRollNumber) {
            const multiresWizardRolls = sessionStorage.getItem("multiresWizard")
            if (multiresWizardRolls) {
                const rolls = multiresWizardRolls.split(",")
                if (!rolls.includes(currentRollNumber)) {
                    sessionStorage.setItem("multiresWizard", multiresWizardRolls + "," + currentRollNumber)
                    const wizardEventPayload: WizardInteractionEventPayload = createWizardEventPayload({
                        wizard_type: "multires",
                        wizard_interaction_type: "enter",
                        wizard_entry_trigger: "once_per_session"
                    })
                    trackWizardEvent(wizardEventPayload)
                }
            } else {
                sessionStorage.setItem("multiresWizard", currentRollNumber)
                const wizardEventPayload: WizardInteractionEventPayload = createWizardEventPayload({
                    wizard_type: "multires",
                    wizard_interaction_type: "enter",
                    wizard_entry_trigger: "once_per_session"
                })
                trackWizardEvent(wizardEventPayload)
            }
        }
    }, [])

    useEffect(() => {
        if (!parkingAssigned && attributesRequested && !profileAttributes.loading && profileAttributes.success) {
            const newParkings = { ...parkings };
            newParkings["residentialIndoorParkingInRent"] = profileAttributes.data.residentialIndoorParkingInRent ?? 0;
            newParkings["residentialIndoorParkingNotInRent"] = profileAttributes.data.residentialIndoorParkingNotInRent ?? 0;
            newParkings["residentialIndoorParkingMonthlyRate"] = profileAttributes.data.residentialIndoorParkingMonthlyRate;
            newParkings["residentialIndoorParkingTotal"] = (newParkings["residentialIndoorParkingInRent"] as number) + (newParkings["residentialIndoorParkingNotInRent"] as number);
            newParkings["residentialSurfaceParkingInRent"] = profileAttributes.data.residentialSurfaceParkingInRent ?? 0;
            newParkings["residentialSurfaceParkingNotInRent"] = profileAttributes.data.residentialSurfaceParkingNotInRent ?? 0;
            newParkings["residentialSurfaceParkingMonthlyRate"] = profileAttributes.data.residentialSurfaceParkingMonthlyRate;
            newParkings["residentialSurfaceParkingTotal"] = (newParkings["residentialSurfaceParkingInRent"] as number) + (newParkings["residentialSurfaceParkingNotInRent"] as number);
            newParkings["residentialParkingTotal"] = (newParkings["residentialSurfaceParkingTotal"] as number) + (newParkings["residentialIndoorParkingTotal"] as number);
            setParkings(newParkings);
            setParkingAssigned(true);
        }
    }, [profileAttributes, attributesRequested, parkingAssigned, parkings])

    useEffect(() => {
        if (propertyType === undefined) {
            toast.error("Error getting property code, wizard closed");
            onCancel((currentWizardPage + 1), "system_error", submitted)
        }
        // eslint-disable-next-line
    }, [propertyType]);

    useEffect(() => {
        if (submitted && (!saveResultState.loading && saveResultState.loadingCount === 0 && !rollDownUnitState.loading)) {
            // Fix PIER2-1829: the api middleware turns any parsable response - including 4xx/5xx - into a SUCCESS action,
            // so "error" only covers network level failures. A failed save has to be detected off the http statuses,
            // otherwise the wizard closes on a save that never persisted and the user re-runs it, duplicating units.
            const savesFailed = saveResultState.statuses.some(status => status !== 200)
            const rollDownFailed = rollDownUnitState.status !== undefined && rollDownUnitState.status !== 200

            if (saveResultState.error || rollDownUnitState.error || savesFailed || rollDownFailed) {
                setSubmitted(false);
                clearErrors();
                if (saveResultState.error || rollDownUnitState.error) { // non-200 responses are already toasted by the page and portal menu save result handlers
                    if (saveResultState.message) {
                        toast.error(saveResultState.message)
                    }
                    if (rollDownUnitState.message) {
                        toast.error(rollDownUnitState.message)
                    }
                }
            }
            else { // Fix: PIER2-1829 - if there is no error, mark wizard success and close it
                onCancel((currentWizardPage + 1), "completed", submitted);
            }
        }
    }, [currentWizardPage, onCancel, clearErrors, saveResultState, submitted, rollDownUnitState]);

    // this hook sends active wizard step to parent component for GA tracking
    useEffect(() => {
        sendActiveStepToParent(currentWizardPage + 1);
    }, [currentWizardPage, sendActiveStepToParent]);

    const resetApartmentNumber = (styleCode: string) => {
        const apartNumber = { ...apartmentNumber };
        apartNumber[styleCode] = (styleCode === apartmentStyles[0]["unitStyleCode"] ? multiresUnitSubtype : multiresRowHouseUnitSubtype
        ).reduce((unitSubtypeRecord, unitSubtype3Option) => ({
            [unitSubtype3Option["unitSubtypeCode"] as string]:
                0, ...unitSubtypeRecord
        }), {});
        setApartmentNumber(apartNumber);
    }

    const updateApartmentNumber = (value: number | null, name: string) => {

        if (value === null) {
            value = 0
        }

        const nameArr = name.split("-");

        if (nameArr.length === 4) {
            const styleCode = nameArr[2];
            const typeCode = nameArr[3];
            (apartmentNumber[styleCode] as Record<string, unknown>)[typeCode] = value
            setApartmentNumber({ ...apartmentNumber })
        }
    }

    const updateParking = (value: number | null, name: string) => {
        const newParkings = { ...parkings };
        let validated = false;
        let warn = false;

        if (value === null) {
            newParkings[name] = 0;
            validated = true
        } else {
            newParkings[name] = value;
            if (name === "residentialParkingTotal") {
                if (newParkings["residentialParkingTotal"] as number < ((newParkings["residentialSurfaceParkingTotal"] as number) + (newParkings["residentialIndoorParkingTotal"] as number))) {
                    newParkings['residentialSurfaceParkingTotal'] = 0;
                    newParkings['residentialSurfaceParkingNotInRent'] = 0;
                    newParkings['residentialSurfaceParkingMonthlyRate'] = null;
                    newParkings['residentialSurfaceParkingInRent'] = 0;
                    newParkings['residentialIndoorParkingTotal'] = 0;
                    newParkings['residentialIndoorParkingNotInRent'] = 0;
                    newParkings['residentialIndoorParkingMonthlyRate'] = null;
                    newParkings['residentialIndoorParkingInRent'] = 0;
                    warn = true;
                }
                if (newParkings["residentialParkingTotal"] as number > 0) {
                    newParkings["residentialSurfaceParkingTotal"] = (newParkings['residentialParkingTotal'] as number) - (newParkings["residentialIndoorParkingTotal"] as number);
                    newParkings["residentialSurfaceParkingInRent"] = (newParkings['residentialSurfaceParkingTotal'] as number) - (newParkings["residentialSurfaceParkingNotInRent"] as number);
                    newParkings["residentialIndoorParkingInRent"] = (newParkings['residentialIndoorParkingTotal'] as number) - (newParkings["residentialIndoorParkingNotInRent"] as number);
                }
                validated = true;
            } else if (name === "residentialIndoorParkingTotal" && ((newParkings["residentialParkingTotal"] as number) >= (newParkings["residentialIndoorParkingTotal"] as number))) {
                if (newParkings["residentialIndoorParkingTotal"] as number < ((newParkings["residentialIndoorParkingNotInRent"] as number) + (newParkings["residentialIndoorParkingInRent"] as number))) {
                    newParkings['residentialIndoorParkingNotInRent'] = 0;
                    newParkings['residentialIndoorParkingMonthlyRate'] = null;
                    newParkings['residentialIndoorParkingInRent'] = 0;
                    warn = true;
                }
                newParkings["residentialIndoorParkingInRent"] = (newParkings['residentialIndoorParkingTotal'] as number) - (newParkings["residentialIndoorParkingNotInRent"] as number);
                newParkings["residentialSurfaceParkingTotal"] = (newParkings['residentialParkingTotal'] as number) - (newParkings["residentialIndoorParkingTotal"] as number);
                if (newParkings["residentialSurfaceParkingTotal"] as number < ((newParkings["residentialSurfaceParkingNotInRent"] as number) + (newParkings["residentialSurfaceParkingInRent"] as number))) {
                    newParkings['residentialSurfaceParkingNotInRent'] = 0;
                    newParkings['residentialSurfaceParkingMonthlyRate'] = null;
                    newParkings['residentialSurfaceParkingInRent'] = 0;
                    // warn = true;
                }
                newParkings["residentialSurfaceParkingInRent"] = (newParkings['residentialSurfaceParkingTotal'] as number) - (newParkings["residentialSurfaceParkingNotInRent"] as number);
                validated = true;
            } else if (name === "residentialIndoorParkingNotInRent" && ((newParkings["residentialIndoorParkingTotal"] as number) >= (newParkings["residentialIndoorParkingNotInRent"] as number))) {
                if (newParkings[name] === 0) {
                    newParkings['residentialIndoorParkingMonthlyRate'] = null;
                    warn = true;
                }
                newParkings['residentialIndoorParkingInRent'] = (newParkings['residentialIndoorParkingTotal'] as number) - (newParkings["residentialIndoorParkingNotInRent"] as number);
                validated = true;
            } else if (name === "residentialSurfaceParkingNotInRent" && ((newParkings["residentialSurfaceParkingTotal"] as number) >= (newParkings["residentialSurfaceParkingNotInRent"] as number))) {
                if (newParkings[name] === 0) {
                    newParkings['residentialSurfaceParkingMonthlyRate'] = null;
                    warn = true;
                }
                newParkings['residentialSurfaceParkingInRent'] = (newParkings['residentialSurfaceParkingTotal'] as number) - (newParkings["residentialSurfaceParkingNotInRent"] as number);
                validated = true;
            }
        }

        if (validated) {
            setParkings(newParkings)
        }

        if (warn) {
            toast.warn(t("data-reset") as string);
        }
    }

    const calculteFinalSuites = (): Record<string, unknown> => {
        const finalSuites: Record<string, unknown> = {};
        let totalSuites = 0;
        if (propertyType === "multires") {
            aptTypeMap.apt.forEach((typeCode, index) => {
                if (typeCode !== "309" && typeCode !== "343" && typeCode !== "352") {
                    const finalCount =
                        Math.max(0, (((apartmentNumber[apartmentStyles[0]["unitStyleCode"] as string] as Record<string, unknown>)[typeCode] as number)
                            - ((typeCode === "306" || typeCode === "307") ? 0 : (typeCode === "308" ? ((apartmentNumber[apartmentStyles[0]["unitStyleCode"] as string] as Record<string, unknown>)["315"] as number)
                                : ((apartmentNumber[apartmentStyles[0]["unitStyleCode"] as string] as Record<string, unknown>)[aptTypeMap.bst[index]] as number)))
                            - ((apartmentNumber[apartmentStyles[0]["unitStyleCode"] as string] as Record<string, unknown>)[aptTypeMap.pen[index]] as number)
                            - ((apartmentNumber[apartmentStyles[0]["unitStyleCode"] as string] as Record<string, unknown>)[aptTypeMap.lrg[index]] as number)
                            - ((apartmentNumber[apartmentStyles[0]["unitStyleCode"] as string] as Record<string, unknown>)[aptTypeMap.sml[index]] as number)));
                    if (finalCount > 0) {
                        if (!((apartmentStyles[0]["unitStyleCode"] as string) in finalSuites)) {
                            finalSuites[apartmentStyles[0]["unitStyleCode"] as string] = {};
                        }
                        totalSuites += finalCount;
                        (finalSuites[apartmentStyles[0]["unitStyleCode"] as string] as Record<string, unknown>)[typeCode] = finalCount
                    }
                }
            });
        }
        for (const [key, unitObject] of Object.entries(apartmentNumber)) {
            for (const [subKey, numberOfUnits] of Object.entries(unitObject as Record<string, unknown>)) {
                if ((propertyType !== "multires" || aptTypeMap.apt.indexOf(subKey) < 0 || ["309", "343", "352"].includes(subKey) || key !== apartmentStyles[0]["unitStyleCode"]) && (numberOfUnits as number) > 0) {
                    if (!(key in finalSuites)) {
                        finalSuites[key] = {};
                    }
                    (finalSuites[key] as Record<string, unknown>)[subKey] = numberOfUnits;
                    totalSuites += numberOfUnits as number;
                }
            }
        }
        return { "total": totalSuites, "finalSuites": finalSuites };
    }

    const parkingElements = hasParking ? [
        <NumberOfParkingsWizard key="wizard-total-number-of-parkings" type="residential-parking-total" propertyName="residentialParkingTotal" parkings={parkings} updateParking={updateParking} />,
        <NumberOfParkingsWizard key="wizard-total-number-of-parkings-indoor" totalPropertyNameFromThePreviousStep="residentialParkingTotal" type="residential-indoor-parking-total" propertyName="residentialIndoorParkingTotal" parkings={parkings} updateParking={updateParking} />,
        <NumberOfParkingsWizard key="wizard-number-of-parkings-indoor-not-in-rent" totalPropertyNameFromThePreviousStep="residentialIndoorParkingTotal" type="residential-indoor-parking-not-in-rent" propertyName="residentialIndoorParkingNotInRent" parkings={parkings} updateParking={updateParking} />,
        <ParkingRateWizard key="wizard-parkings-indoor-rate" totalPropertyNameFromThePreviousStep="residentialIndoorParkingNotInRent" type="residential-indoor-parking-monthly-rate" propertyName="residentialIndoorParkingMonthlyRate" parkings={parkings} setParkings={setParkings} />,
        <NumberOfParkingsWizard key="wizard-number-of-parkings-surface-not-in-rent" totalPropertyNameFromThePreviousStep="residentialSurfaceParkingTotal" type="residential-surface-parking-not-in-rent" propertyName="residentialSurfaceParkingNotInRent" parkings={parkings} updateParking={updateParking} />,
        <ParkingRateWizard key="wizard-parkings-surface-rate" totalPropertyNameFromThePreviousStep="residentialSurfaceParkingNotInRent" type="residential-surface-parking-monthly-rate" propertyName="residentialSurfaceParkingMonthlyRate" parkings={parkings} setParkings={setParkings} />
    ] : [];

    const summaryElement = ((confirmImport === "import" || confirmImport === "affordable") && !hasParking) ?
        <ConfirmUnitsWizard key="wizard-rent-units" units={units} setUnits={setUnits} confirmedArr={confirmedArr} setConfirmedArr={setConfirmedArr} isBasePropType={props.isBasePropType} multiresUnitSubtype={multiresUnitSubtype} multiresRowHouseUnitSubtype={multiresRowHouseUnitSubtype} styleOptions={props.styleOptions} unitTypeSubtypeMap={props.unitTypeSubtypeMap} bathsOptions={props.bathsOptions} purpose={confirmImport === "affordable" ? 'affordable-summary' : 'summary'} />
        : <MultiresSummaryWizard key="wizard-multires-summary" parkings={parkings} calculteFinalSuites={calculteFinalSuites} hasParking={hasParking} isBasePropType={props.isBasePropType} styleOptions={props.styleOptions} unitTypeSubtypeMap={props.unitTypeSubtypeMap} multiresUnitSubtype={multiresUnitSubtype} multiresRowHouseUnitSubtype={multiresRowHouseUnitSubtype} />

    const orderedWizardElements = confirmImport === "import" ?
        [
            <ConfirmUnitsWizard key="wizard-confirm-units" units={units} setUnits={setUnits} confirmedArr={confirmedArr} setConfirmedArr={setConfirmedArr} isBasePropType={props.isBasePropType ?? false} multiresUnitSubtype={multiresUnitSubtype} multiresRowHouseUnitSubtype={multiresRowHouseUnitSubtype} styleOptions={props.styleOptions} unitTypeSubtypeMap={props.unitTypeSubtypeMap} bathsOptions={props.bathsOptions} purpose='confirm' />,
            <ConfirmUnitsWizard key="wizard-edit-units" units={units} setUnits={setUnits} confirmedArr={confirmedArr} setConfirmedArr={setConfirmedArr} isBasePropType={props.isBasePropType ?? false} multiresUnitSubtype={multiresUnitSubtype} multiresRowHouseUnitSubtype={multiresRowHouseUnitSubtype} styleOptions={props.styleOptions} unitTypeSubtypeMap={props.unitTypeSubtypeMap} bathsOptions={props.bathsOptions} purpose='edit' />,
            <ConfirmUnitsWizard key="wizard-rent-units" units={units} setUnits={setUnits} confirmedArr={confirmedArr} setConfirmedArr={setConfirmedArr} isBasePropType={props.isBasePropType ?? false} multiresUnitSubtype={multiresUnitSubtype} multiresRowHouseUnitSubtype={multiresRowHouseUnitSubtype} styleOptions={props.styleOptions} unitTypeSubtypeMap={props.unitTypeSubtypeMap} bathsOptions={props.bathsOptions} purpose='rent' />,
            ...parkingElements,
            summaryElement
        ] : (confirmImport === "affordable" ?
            (
                hasParking ?
                    [
                        ...parkingElements,
                        summaryElement
                    ] : [summaryElement]
            ) :
            [
                <ApartmentStyleWizard key="wizard-apartment-style" apartmentStyles={apartmentStyles} apartmentStyleArray={apartmentStyleArray} setApartmentStyleArray={setApartmentStyleArray} resetApartmentNumber={resetApartmentNumber} />,
                <ApartmentUnitsWizard key="wizard-apartment-units-apt" questionType='apt' apartmentStyles={apartmentStyles} multiresUnitSubtype={multiresUnitSubtype} apartmentNumber={apartmentNumber} updateApartmentNumber={updateApartmentNumber} aptTypeMap={aptTypeMap} />,
                <ApartmentUnitsWizard key="wizard-apartment-units-bst" questionType='bst' apartmentStyles={apartmentStyles} multiresUnitSubtype={multiresUnitSubtype} apartmentNumber={apartmentNumber} updateApartmentNumber={updateApartmentNumber} aptTypeMap={aptTypeMap} />,
                <ApartmentUnitsWizard key="wizard-apartment-units-pen" questionType='pen' apartmentStyles={apartmentStyles} multiresUnitSubtype={multiresUnitSubtype} apartmentNumber={apartmentNumber} updateApartmentNumber={updateApartmentNumber} aptTypeMap={aptTypeMap} />,
                <ApartmentUnitsWizard key="wizard-apartment-units-lrg" questionType='lrg' apartmentStyles={apartmentStyles} multiresUnitSubtype={multiresUnitSubtype} apartmentNumber={apartmentNumber} updateApartmentNumber={updateApartmentNumber} aptTypeMap={aptTypeMap} />,
                <ApartmentUnitsWizard key="wizard-apartment-units-sml" questionType='sml' apartmentStyles={apartmentStyles} multiresUnitSubtype={multiresUnitSubtype} apartmentNumber={apartmentNumber} updateApartmentNumber={updateApartmentNumber} aptTypeMap={aptTypeMap} />,
                <OtherUnitsWizard key="wizard-other-units" apartmentStyles={apartmentStyles} multiresRowHouseUnitSubtype={multiresRowHouseUnitSubtype} apartmentNumber={apartmentNumber} updateApartmentNumber={updateApartmentNumber} aptTypeMap={aptTypeMap} apartmentStyleArray={apartmentStyleArray} />,
                ...parkingElements,
                summaryElement
            ])

    const pagesToGoThrough = (advancing: boolean): number => {
        let pagesToAdvance = 1;
        let checkingCondition = true;
        while (checkingCondition) {
            checkingCondition = false;
            if (orderedWizardElements[advancing ? (currentWizardPage + pagesToAdvance) : (currentWizardPage - pagesToAdvance)].key === "wizard-total-number-of-parkings-indoor" && parkings["residentialParkingTotal"] === 0) {
                pagesToAdvance += 1;
                checkingCondition = true;
            }
            if (orderedWizardElements[advancing ? (currentWizardPage + pagesToAdvance) : (currentWizardPage - pagesToAdvance)].key === "wizard-number-of-parkings-indoor-not-in-rent" && parkings["residentialIndoorParkingTotal"] === 0) {
                pagesToAdvance += 1;
                checkingCondition = true;
            }
            if (orderedWizardElements[advancing ? (currentWizardPage + pagesToAdvance) : (currentWizardPage - pagesToAdvance)].key === "wizard-parkings-indoor-rate" && parkings["residentialIndoorParkingNotInRent"] === 0) {
                pagesToAdvance += 1;
                checkingCondition = true;
            }
            if (orderedWizardElements[advancing ? (currentWizardPage + pagesToAdvance) : (currentWizardPage - pagesToAdvance)].key === "wizard-number-of-parkings-surface-not-in-rent" && parkings["residentialSurfaceParkingTotal"] === 0) {
                pagesToAdvance += 1;
                checkingCondition = true;
            }
            if (orderedWizardElements[advancing ? (currentWizardPage + pagesToAdvance) : (currentWizardPage - pagesToAdvance)].key === "wizard-parkings-surface-rate" && parkings["residentialSurfaceParkingNotInRent"] === 0) {
                pagesToAdvance += 1;
                checkingCondition = true;
            }
        }
        return pagesToAdvance;
    }

    const previousWizardPage = () => {
        if (orderedWizardElements[currentWizardPage - 1].key === "wizard-other-units" && !apartmentStyleArray["2"] && !apartmentStyleArray["3"]) {
            setCurrentWizardPage(currentWizardPage - ((!apartmentStyleArray["1"]) ? 7 : 2));
        }
        else if (orderedWizardElements[currentWizardPage - 1].key === "wizard-apartment-units-sml" && !apartmentStyleArray["1"]) {
            setCurrentWizardPage(currentWizardPage - 6);
        } else if (orderedWizardElements[currentWizardPage - 1].key === "wizard-edit-units" && units.length === confirmedArr.length) {
            setCurrentWizardPage(currentWizardPage - 2);
        } else {
            setCurrentWizardPage(currentWizardPage - pagesToGoThrough(false));
        }
    }

    const nextWizardPage = () => {
        if (orderedWizardElements[currentWizardPage].key === "wizard-apartment-style" && !apartmentStyleArray["1"]) {
            setCurrentWizardPage(currentWizardPage + ((!apartmentStyleArray["2"] && !apartmentStyleArray["3"]) ? 7 : 6));
        } else if (orderedWizardElements[currentWizardPage + 1].key === "wizard-other-units" && !apartmentStyleArray["2"] && !apartmentStyleArray["3"]) {
            setCurrentWizardPage(currentWizardPage + 2);
        } else if (orderedWizardElements[currentWizardPage + 1].key === "wizard-edit-units" && units.length === confirmedArr.length) {
            setCurrentWizardPage(currentWizardPage + 2);
        } else {
            setCurrentWizardPage(currentWizardPage + pagesToGoThrough(true));
        }
    }

    const submitMultiresData = (e: React.MouseEvent<HTMLButtonElement>) => {
        e.preventDefault();
        if (submitted) { // Fix PIER2-1829: a submit is already in flight, re-submitting would duplicate the units
            return
        }
        // Fix PIER2-1829: clear results of earlier saves so the statuses/status checked when this submit settles only describe this submit
        dispatch(resetSaveResult());
        dispatch(resetRollDownIncomeUnits());
        const finalSuites = calculteFinalSuites().finalSuites;
        const generated: Record<string, unknown>[] = []

        if (confirmImport === "new") {
            for (const [suiteType, numberObject] of Object.entries(finalSuites as Record<string, unknown>)) {
                for (const [typeCode, amt] of Object.entries(numberObject as Record<string, unknown>)) {
                    if (props.showAffordableHousingQuestion && hasAffordableHousingFlag) {
                        for (let i = 0; i < (amt as number); i++) {
                            const newUnit = {
                                ...getDefaultUnit(),
                                unitTypeCode: '3',
                                unitSubtypeCode: typeCode,
                                styleTypeCode: suiteType,
                                bathCount: null,
                                netLeasableArea: '',
                                monthlyMarketRent: null,
                                new: true,
                                unitCount: 1
                            }
                            generated.push(newUnit)
                        }
                    } else if (["BIP", "GLF", "MTL"].includes(profileOverview.profile.profile_type_code)) {
                        const newUnit = {
                            ...getDefaultUnit(),
                            unitTypeCode: '3',
                            unitSubtypeCode: typeCode,
                            styleTypeCode: suiteType,
                            unitCount: amt,
                            bathCount: null,
                            netLeasableArea: '',
                            monthlyMarketRent: null,
                            new: true
                        }
                        generated.push(newUnit)
                    } else {
                        for (let i = 0; i < (amt as number); i++) {
                            const newUnit = {
                                ...getDefaultUnit(),
                                unitTypeCode: '3',
                                unitSubtypeCode: typeCode,
                                styleTypeCode: suiteType,
                                bathCount: null,
                                netLeasableArea: '',
                                monthlyMarketRent: null,
                                new: true
                            }
                            generated.push(newUnit)
                        }
                    }
                }
            }

            const wizardEventPayload: WizardInteractionEventPayload = createWizardEventPayload({
                wizard_type: "multires",
                wizard_interaction_type: "submit",
                wizard_units_saved: generated.length,
                wizard_step: currentWizardPage + 1
            })
            trackWizardEvent(wizardEventPayload)
        }
        if (confirmImport === "import") {
            dispatch(upsertBatchIncomeUnits(props.snapshotId, {
                "added": units.filter((unit) => unit.tenantId === null || unit.tenantId === undefined),
                "updated": units.filter((unit) => unit.tenantId !== null && unit.tenantId !== undefined),
                "property_code": sessionStorage.getItem("propertyCode") as string,
                "profile_type_code": profileOverview.profile.profile_type_code,
                "trigger_spectral": false
            }))

            const wizardEventPayload: WizardInteractionEventPayload = createWizardEventPayload({
                wizard_type: "multires",
                wizard_interaction_type: "submit",
                wizard_units_saved: units.length,
                wizard_step: currentWizardPage + 1
            })
            trackWizardEvent(wizardEventPayload)

        } else if (generated.length > 0) {
            dispatch(upsertBatchIncomeUnits(props.snapshotId, {
                "added": generated,
                "property_code": sessionStorage.getItem("propertyCode") as string,
                "profile_type_code": profileOverview.profile.profile_type_code,
                "trigger_spectral": false
            }))

            const wizardEventPayload: WizardInteractionEventPayload = createWizardEventPayload({
                wizard_type: "multires",
                wizard_interaction_type: "submit",
                wizard_units_saved: generated.length,
                wizard_step: currentWizardPage + 1
            })
            trackWizardEvent(wizardEventPayload)
        }
        if (props.showAffordableHousingQuestion || props.showResidentialQuestion) {
            dispatch(resetSaving())
            removeError(getFormErrorByName(errors, "hasApartmentFlag" as string)?.primaryKey as string)
            removeError(getFormErrorByName(errors, "hasAffordableHousingFlag" as string)?.primaryKey as string)
            dispatch(putAptFlagAffordableHousing(props.snapshotId, {
                "has_apartment_flag": props.hasApartmentFlag,
                "has_affordable_housing_flag": hasAffordableHousingFlag,
                "affordable_housing_agreement_codes": hasAffordableHousingFlag ? props.affordableHousingFields.affordableHousingAgreementCodes : null,
                "affordable_housing_count": hasAffordableHousingFlag ? props.affordableHousingFields.affordableHousingCount : null,
                "spectral_section": "-res-units",
                "trigger_spectral": false,
                "property_code": props.propertyCode,
                "show_residential_question": props.showResidentialQuestion,
                "show_affordable_housing_question": props.showAffordableHousingQuestion
            }))
            if (hasAffordableHousingFlag === true) {
                console.log("MultiresWizard trigger reset all units")
                props.rollDownAllUnitsTrigger(true)
            }
        }
        if (hasParking) {
            const finalParkings = {
                'residentialSurfaceParkingNotInRent': parkings['residentialSurfaceParkingNotInRent'] === 0 ? null : parkings['residentialSurfaceParkingNotInRent'],
                'residentialSurfaceParkingMonthlyRate': parkings['residentialSurfaceParkingMonthlyRate'],
                'residentialSurfaceParkingInRent': parkings['residentialSurfaceParkingInRent'] === 0 ? null : parkings['residentialSurfaceParkingInRent'],
                'residentialIndoorParkingNotInRent': parkings['residentialIndoorParkingNotInRent'] === 0 ? null : parkings['residentialIndoorParkingNotInRent'],
                'residentialIndoorParkingMonthlyRate': parkings['residentialIndoorParkingMonthlyRate'],
                'residentialIndoorParkingInRent': parkings['residentialIndoorParkingInRent'] === 0 ? null : parkings['residentialIndoorParkingInRent']
            }
            const fulldata = { ...profileAttributes.data, ...finalParkings } as Record<string, unknown>
            dispatch(putProfileAttributes(profileOverview.profile.profile_type_code, props.snapshotId, {
                "profile_attributes": fulldata,
                "spectral_section": "res_parking",
                "trigger_spectral": false
            }));
        }
        setSubmitted(true);
    }

    const cancelWizard = () => {
        onCancel((currentWizardPage + 1), "cancel_button", submitted);
    }

    const removeAllUnits = () => {
        console.log("MultiresWizard trigger delete all units")
        props.deleteAllUnitsTrigger(true)
    }

    const rolldownUnitConfirmAction = () => {
        setShowRollDownWarning(false);
        setConfirmImport(confirmImport === "import" ? "affordable" : "new")
        setShowAffordableHousingQuestion(false)
        setHasAffordableHousingFlag(true)
    }

    return (
        <>
            <LoadingSpinner loading={saveResultState.loading || profileAttributes.loading || deleteUnitsState.loading || rollDownUnitState.loading || props.profileDetails.loading || !props.snapshotId}>
                <div className='guided-experience-wizard multires-wizard'>
                    {confirmImport === "question" ?
                        <ConfirmDeleteWizard setConfirmImport={setConfirmImport} setShowWarningModal={setShowDeleteWarning} />
                        : (showAffordableHousingQuestion ?
                            <AffordableHousingWizard setHasAffordableHousingFlag={setHasAffordableHousingFlag}
                                setShowRollDownWarning={setShowRollDownWarning}
                                setShowAffordableHousingQuestion={setShowAffordableHousingQuestion}
                                unitData={props.unitData}
                                rolldownUnitConfirmAction={rolldownUnitConfirmAction} />
                            : <>
                                <div className='multires-wizard-content'>
                                    {(confirmImport === "new" && currentWizardPage === 0) && <p>{t("multires-wizard.multires-heading")}</p>}
                                    <p>{t("step-1") + (currentWizardPage + 1) + t("step-2") + orderedWizardElements.length}</p>
                                    {orderedWizardElements[currentWizardPage]}
                                </div>
                                <div className='guided-experience-wizard-buttons'>
                                    <Button type='cancel' onClick={cancelWizard} >
                                        {t("cancel").toLocaleUpperCase(i18n.language)}
                                    </Button>
                                    {currentWizardPage !== 0 && <Button type='secondary' onClick={() => previousWizardPage()} testId='wizard-previous-button'>
                                        {t("back").toLocaleUpperCase(i18n.language)}
                                    </Button>}
                                    {currentWizardPage !== (orderedWizardElements.length - 1) && <Button type='secondary' onClick={() => nextWizardPage()} testId='wizard-next-button' >
                                        {t("next").toLocaleUpperCase(i18n.language)}
                                    </Button>}
                                    {currentWizardPage === (orderedWizardElements.length - 1) && <Button type='submit' onClick={submitMultiresData} testId='wizard-submit-button' >
                                        {t("submit").toLocaleUpperCase(i18n.language)}
                                    </Button>}
                                </div>
                            </>)}
                </div>
            </LoadingSpinner>
            {showDeleteWarning &&
                <Modal>
                    <div className='multires-wizard-warning-modal'>
                        <p>{t("multires-wizard.delete-warning")}</p>
                        <div className='guided-wizard-warning-buttons'>
                            <Button type='secondary' onClick={() => {
                                removeAllUnits();
                                setShowDeleteWarning(false);
                                setConfirmImport("new")
                            }}
                                className='guided-wizard-warning-cancel'
                                testId='wizard-delete-all-confirm'>
                                {t("confirm").toLocaleUpperCase(i18n.language)}
                            </Button>
                            <Button type='cancel' onClick={() => setShowDeleteWarning(false)} testId='wizard-delete-all-cancel'>
                                {t("cancel").toLocaleUpperCase(i18n.language)}
                            </Button>
                        </div>
                    </div>
                </Modal>
            }
            {showRollDownWarning &&
                <Modal>
                    <div className='multires-wizard-warning-modal'>
                        <p>
                            <Trans t={t}
                                i18nKey="multires-wizard.reset-warning"
                                components={{ u: <u /> }}
                            />
                        </p>
                        <div className='guided-wizard-warning-buttons'>
                            <Button type='secondary' onClick={() => rolldownUnitConfirmAction()}
                                className='guided-wizard-warning-cancel'
                                testId='wizard-roll-down-confirm'>
                                {t("confirm").toLocaleUpperCase(i18n.language)}
                            </Button>
                            <Button type='cancel' onClick={() => setShowRollDownWarning(false)} testId='wizard-roll-down-cancel'>
                                {t("cancel").toLocaleUpperCase(i18n.language)}
                            </Button>
                        </div>
                    </div>
                </Modal>
            }
        </>
    )
}
